import React, { useState, useEffect } from 'react';
import './AllProjects.css';

const AllProjects = ({ data }) => {
  const allProjectsFlat = data.allProjects.flatMap(yearGroup =>
    yearGroup.projects.map(project => ({
      ...project,
      year: yearGroup.year
    }))
  );

  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isPaused, setIsPaused] = useState(false);

  const minSwipeDistance = 50;

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % allProjectsFlat.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + allProjectsFlat.length) % allProjectsFlat.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) nextSlide();
    if (isRightSwipe) prevSlide();
  };

  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % allProjectsFlat.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isPaused, allProjectsFlat.length]);

  return (
    <section className="all-projects">
      <div className="section-header-fullwidth">
        <div className="container">
          <h2 className="section-title">All Projects</h2>
          <p className="section-subtitle">
            A comprehensive timeline of my work
          </p>
        </div>
      </div>
      
      <div className="container">
        <div 
          className="projects-slider"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div className="slider-wrapper">
            <div 
              className="slider-track"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {allProjectsFlat.map((project, index) => (
                <div key={index} className="slider-slide">
                  <div className="project-year-badge">{project.year}</div>
                  <div className="timeline-project card">
                    <h4 className="project-title">{project.title}</h4>
                    <p className="project-description">{project.description}</p>
                    <div className="project-meta">
                      {project.role && (
                        <div className="meta-item">
                          <strong>Role:</strong> {project.role}
                        </div>
                      )}
                      {project.agency && (
                        <div className="meta-item">
                          <strong>Agency:</strong> {project.agency}
                        </div>
                      )}
                      {project.client && (
                        <div className="meta-item">
                          <strong>Client:</strong> {project.client}
                        </div>
                      )}
                      {project.url && (
                        <div className="meta-item">
                          <strong>URL:</strong>{' '}
                          <a href={project.url} target="_blank" rel="noopener noreferrer" className="project-link">{project.url}</a>
                        </div>
                      )}
                      {project.youtubeUrl && (
                        <div className="meta-item">
                          <strong>Video:</strong>{' '}
                          <a href={project.youtubeUrl} target="_blank" rel="noopener noreferrer" className="project-link">Watch on YouTube</a>
                        </div>
                      )}
                      {project.awards && (
                        <div className="meta-item awards">
                          <strong>Awards:</strong> {project.awards}
                        </div>
                      )}
                    </div>
                    {project.tech && (
                      <div className="project-tech">
                        <strong>Tech:</strong>
                        <div className="tech-tags">
                          {project.tech.split(',').map((tech, i) => (
                            <span key={i} className="tech-tag">{tech.trim()}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button 
            className="slider-btn slider-btn-prev" 
            onClick={prevSlide}
            aria-label="Previous project"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
          </button>
          <button 
            className="slider-btn slider-btn-next" 
            onClick={nextSlide}
            aria-label="Next project"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </button>

          <div className="slider-indicators">
            {allProjectsFlat.map((_, index) => (
              <button
                key={index}
                className={`slider-indicator ${index === currentSlide ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          <div className="slider-counter">
            {currentSlide + 1} / {allProjectsFlat.length}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AllProjects;
